import { FC, useContext, useRef, useState } from 'react';

import {
  ContextMenu,
  Pagination,
  Table,
  useContextMenu,
} from '@smartshell/ui-kit';
import cl from 'classnames';
import { HeaderGroup } from 'react-table';

import { AuthContext } from 'auth';
import { LayoutContext } from 'components/Layout';
import { useOnClickOutside } from 'hooks';
import {
  ClientModal,
  DepositModal,
  NotPermissionModal,
  ResetPasswordModal,
} from 'modals';
import s from 'pages/Clients/Clients.module.scss';
import { ESortDirection, TClient } from 'pages/Clients/interfaces';
import { IUser } from 'types';

import { TableProps } from '../interfaces';
import { useColumns } from './useColumns';
import { useContextMenuItems } from './useContextMenuItems';

export const TableComponent: FC<TableProps> = ({
  groups,
  getClients,
  data,
  loading,
  variables,
  selectedClientsUUIDs,
  setSelectedClientsUUIDs,
}) => {
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const contextMenuRef = useRef(null);
  const {
    data: clientsList,
    paginatorInfo: { lastPage = 0 },
  } = data?.clients || {
    data: [],
    paginatorInfo: { lastPage: undefined },
  };

  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');
  const [isResetPasswordModal, setIsResetPasswordModal] = useState<
    boolean | undefined
  >(false);
  const [currentClient, setCurrentClient] = useState<IUser | undefined>(
    undefined
  );
  const [doubleClickedClientUUID, setDoubleClickedClientUUID] = useState<
    string | null
  >(null);
  const [notPermissionModalOpen, setNotPermissionModalOpen] =
    useState<boolean>(false);
  const [lastSelectedRowIndex, setLastSelectedRowIndex] = useState<
    number | null
  >(null);

  const layoutElement = useContext(LayoutContext);
  const contextMenuItems = useContextMenuItems(
    groups,
    data,
    selectedClientsUUIDs,
    setSelectedClientsUUIDs,
    setDoubleClickedClientUUID,
    setNotPermissionModalOpen
  );

  const {
    contextMenuData,
    closeContextMenu,
    cursorPosition,
    onContextMenu,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  } = useContextMenu<TClient>();

  useOnClickOutside(contextMenuRef, closeContextMenu);

  const columns = useColumns(selectedClientsUUIDs, setSelectedClientsUUIDs);

  return (
    <>
      {contextMenuData && cursorPosition && (
        <div className={cl(s.contextMenu)} ref={contextMenuRef}>
          <ContextMenu
            layoutElement={layoutElement}
            data={contextMenuData}
            items={contextMenuItems}
            closeContextMenu={closeContextMenu}
            position={cursorPosition}
          />
        </div>
      )}
      {isResetPasswordModal && (
        <ResetPasswordModal
          currentClient={currentClient}
          onClose={() => setIsResetPasswordModal(false)}
          clientUUID={doubleClickedClientUUID}
        />
      )}
      {isDepositOpen && (
        <DepositModal
          defaultClientUUID={defaultClient}
          onClose={() => {
            setIsDepositOpen(false);
            setDoubleClickedClientUUID(null);
          }}
        />
      )}
      {notPermissionModalOpen && (
        <NotPermissionModal onClose={() => setNotPermissionModalOpen(false)} />
      )}
      {doubleClickedClientUUID && !isDepositOpen && !isResetPasswordModal && (
        <ClientModal
          clientUUID={doubleClickedClientUUID}
          onClose={() => {
            setDoubleClickedClientUUID(null);
          }}
          setIsDepositOpen={setIsDepositOpen}
          setIsDefaultClient={setIsDefaultClient}
          setIsResetPasswordModal={setIsResetPasswordModal}
          setCurrentClient={setCurrentClient}
        />
      )}
      <div className={s.table}>
        <Table
          data={clientsList}
          columns={columns}
          isLoading={loading}
          sortBy={[{ id: 'last_client_activity', desc: true }]}
          onRowClick={(event, row, table) => {
            const flatRows = table.flatRows;
            const currentRowIndex = flatRows.findIndex((r) => r.id === row.id);

            if (event.shiftKey) {
              if (lastSelectedRowIndex === null) {
                setLastSelectedRowIndex(currentRowIndex);
              }

              const safeLastSelectedRowIndex =
                lastSelectedRowIndex ?? currentRowIndex;

              const startIndex = Math.min(
                safeLastSelectedRowIndex,
                currentRowIndex
              );
              const endIndex = Math.max(
                safeLastSelectedRowIndex,
                currentRowIndex
              );

              flatRows.forEach((r) => r.toggleRowSelected(false));

              const rowsToSelect = flatRows.slice(startIndex, endIndex + 1);
              rowsToSelect.forEach((r) => r.toggleRowSelected(true));

              setSelectedClientsUUIDs(
                rowsToSelect.map((row) => row.original.uuid)
              );
            } else if (
              event.button === 0 ||
              (event.button === 2 && !row.isSelected)
            ) {
              setSelectedClientsUUIDs((prevUUIDs) => {
                return row.isSelected
                  ? prevUUIDs.filter((item) => item !== row.original.uuid)
                  : prevUUIDs.concat(row.original.uuid);
              });
              row.toggleRowSelected();

              setLastSelectedRowIndex(currentRowIndex);
            }
          }}
          onRowDoubleClick={(_, client) => {
            if (!permissions?.has('client_info')) {
              setNotPermissionModalOpen(true);
              return;
            }
            setDoubleClickedClientUUID(client.uuid);
          }}
          onContextMenu={(event, data) => {
            onContextMenu(event, data);
          }}
          onTouchStart={(event, row) => {
            onTouchStart(event, row);
          }}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onHeaderClick={(column: HeaderGroup) => {
            if (!column.disableSortBy) {
              Promise.resolve().then(() => {
                const direction = column.isSortedDesc
                  ? ESortDirection.DESC
                  : column.isSorted
                  ? ESortDirection.ASC
                  : ESortDirection.DESC;

                if (!permissions?.has('client_browse')) return;

                getClients({
                  variables: {
                    ...variables,
                    page: 1,
                    input: {
                      ...variables?.input,
                      sort: {
                        direction,
                        field: column.id,
                      },
                    },
                  },
                });
              });
            }
          }}
          className={s.table}
          manualSortBy
        />
      </div>
      <Pagination
        className={s.pagination}
        onChange={(page: number) => {
          if (!permissions?.has('client_browse')) return;

          getClients({
            variables: {
              ...variables,
              page,
              input: {
                ...variables?.input,
              },
            },
          });
        }}
        lastPage={lastPage}
        currentPage={variables?.page ?? 1}
      />
    </>
  );
};
